/**
 * DetailShell — shared T2 entity-detail shell (Kopfbereich + tab bar + content).
 * Reused by KundeDetailScreen, PoliceDetailScreen, VersichererDetailScreen.
 * Ported from v0 kunden-view.tsx KundeDetail header + tab logic.
 * [Source: docs/stories/t2-entity-detail.md#task-2]
 */
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ChevronLeft } from 'lucide-react';
import { useCanGoBack, useRouter } from '@tanstack/react-router';
import { CountBadge } from '../../../components/ui/CountBadge';
import { StickyPageHeader } from '../../../components/ui/StickyPageHeader';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/Tabs';
import { TabOverflowMenu, type OverflowTab } from './TabOverflowMenu';
import { ActionsMenu, type ActionItem } from './ActionsMenu';
import { cn } from '../../../lib/cn';

/**
 * History-aware "Zurück" for the detail header (C1 Defect 2).
 *
 * When the user reached this detail page from another in-app page — e.g. clicked a Kunde/Police
 * card in the PDF viewer (`/ablage/:id`) — `router.history.back()` returns to THAT exact page
 * (the viewer), mirroring the AppShell topbar "Zurück". Only when there is no in-app history to
 * go back to (a fresh load / deep link) do we fall back to the provided parent-list target
 * (`backTo.onClick`, e.g. → /policen). Rendered ONLY when `backTo` is set, so the shell stays
 * usable without a router (its unit tests render it router-free).
 */
function DetailBackButton({ backTo }: { backTo: { label: string; onClick: () => void } }) {
  const router = useRouter();
  const canGoBack = useCanGoBack();
  return (
    <div className="px-1 pb-2">
      <button
        type="button"
        onClick={() => (canGoBack ? router.history.back() : backTo.onClick())}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ChevronLeft className="size-3.5" />
        {backTo.label}
      </button>
    </div>
  );
}

export interface TabConfig {
  value: string;
  label: string;
  icon?: LucideIcon;
  count?: number;
}

interface DetailShellProps {
  /** Left side icon component (e.g. Building2) */
  icon: LucideIcon;
  iconClassName?: string;
  /** Entity title (string or e.g. <span className="mono-num">) */
  title: ReactNode;
  /** Status element (e.g. <StatusDot .../>), rendered inline after the title. */
  status?: ReactNode;
  /**
   * E1/E4: the status block for the TOP RIGHT of the header (the control plus
   * the "seit" date under it). Distinct from `status`, which is the inline badge
   * the Police and Versicherer pages still use: only the client page moves its
   * status to the right and makes it editable.
   */
  statusBlock?: ReactNode;
  /** Extra badge (e.g. Risiko) after status */
  extraBadge?: ReactNode;
  /** Sub-line of meta info (branche · ort · ...) */
  meta?: ReactNode;
  /** Key number label (e.g. "Jahresprämie") */
  keyNumberLabel?: string;
  /** Key number value (e.g. chf(102500)) */
  keyNumber?: ReactNode;
  /** Primary action button (rendered next to key number) */
  primaryAction?: ReactNode;
  /** Actions for the ⋯ overflow menu */
  actions?: ActionItem[];
  /** Back link config */
  backTo?: { label: string; onClick: () => void };
  /** Tab configuration */
  tabs: TabConfig[];
  /** How many tabs to show directly (rest fold into "Mehr") */
  primaryCount?: number;
  activeTab: string;
  onTabChange: (value: string) => void;
  children: ReactNode;
}

export function DetailShell({
  icon: Icon,
  iconClassName = 'bg-foreground text-background',
  title,
  status,
  statusBlock,
  extraBadge,
  meta,
  keyNumberLabel,
  keyNumber,
  primaryAction,
  actions = [],
  backTo,
  tabs,
  primaryCount = 5,
  activeTab,
  onTabChange,
  children,
}: DetailShellProps) {
  const visibleTabs = tabs.slice(0, primaryCount);
  const overflowTabs: OverflowTab[] = tabs.slice(primaryCount).map((t) => ({
    value: t.value,
    label: t.label,
    count: t.count,
  }));

  return (
    /*
     * E3: back link + Kopfbereich + tab bar are ONE pinned block, so <Tabs> has
     * to wrap all of it (TabsList reads its context). `-mx-4 px-4` reaches back
     * over the p-4 each consumer screen applies, covering the page gutters.
     */
    <Tabs value={activeTab} onValueChange={onTabChange} className="gap-0">
      <StickyPageHeader className="-mx-4 px-4">
      {/* Optional back link — history-aware (returns to the exact previous in-app page, e.g. the
          PDF viewer a connected-object card was clicked from; falls back to the parent list). */}
      {backTo && <DetailBackButton backTo={backTo} />}

      {/* Kopfbereich */}
      <div className="flex flex-col gap-4 border-b pb-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          {/* Left: icon + title + status + meta */}
          <div className="flex items-center gap-3">
            <span
              className={cn(
                'flex size-12 shrink-0 items-center justify-center rounded-lg',
                iconClassName,
              )}
            >
              <Icon className="size-5" />
            </span>
            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold text-foreground">{title}</h2>
                {status}
                {extraBadge}
              </div>
              {meta && <span className="text-sm text-muted-foreground">{meta}</span>}
            </div>
          </div>

          {/* Right: status block (E1/E4) + key number + primary action + ⋯ */}
          <div className="flex items-center gap-4">
            {statusBlock}
            {(keyNumber || keyNumberLabel) && (
              <div className="flex flex-col items-end">
                {keyNumberLabel && (
                  <span className="text-xs text-muted-foreground">{keyNumberLabel}</span>
                )}
                {keyNumber && (
                  <span className="text-xl font-semibold mono-num text-foreground">
                    {keyNumber}
                  </span>
                )}
              </div>
            )}
            <div className="flex items-center gap-2">
              {primaryAction}
              {actions.length > 0 && <ActionsMenu actions={actions} />}
            </div>
          </div>
        </div>
      </div>

      {/* Tab bar (inside the pinned block) */}
      <div className="mt-4">
          {/* Underline tabs — content sections within ONE entity (design.md §5). */}
          <TabsList variant="underline" className="w-full justify-start gap-4 overflow-x-auto">
            {visibleTabs.map((t) => {
              const TabIcon = t.icon;
              return (
                <TabsTrigger key={t.value} value={t.value} className="flex-none">
                  {TabIcon && <TabIcon className="size-3.5" />}
                  {t.label}
                  {t.count !== undefined && <CountBadge count={t.count} className="ml-1 h-4" />}
                </TabsTrigger>
              );
            })}
            {overflowTabs.length > 0 && (
              <TabOverflowMenu
                tabs={overflowTabs}
                activeTab={activeTab}
                onSelect={onTabChange}
              />
            )}
          </TabsList>
      </div>
      </StickyPageHeader>

      {/* Tab content rendered by consumer (scrolls under the pinned block) */}
      <div className="pt-5">{children}</div>
    </Tabs>
  );
}
